import axios from "axios";
import { toast } from "react-toastify"; // Import react-toastify

const getToken = () => localStorage.getItem("token");

const getApiErrorMessage = (data) => {
  const message = data?.error?.message ?? data?.message;

  if (Array.isArray(message)) {
    return message[0] ?? null;
  }

  if (typeof message === "string" && message.trim()) {
    return message;
  }

  return null;
};

const getBlobApiErrorMessage = async (data) => {
  if (!(typeof Blob !== "undefined" && data instanceof Blob)) {
    return null;
  }

  try {
    const text = await data.text();

    if (!text) {
      return null;
    }

    return getApiErrorMessage(JSON.parse(text));
  } catch {
    return null;
  }
};

const setToken = (token, isAdmin) => {
  if (isAdmin) {
    localStorage.setItem("adminToken", token);
  } else {
    localStorage.setItem("token", token);
  }
};

let cancelTokenSource;

let axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASEURL + "api",
  headers: {
    "Content-type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    cancelTokenSource = axios.CancelToken.source();
    config.cancelToken = cancelTokenSource.token;
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const responseData = error.response?.data;

    if (responseData?.status === "Error") {
      const errorMessage = responseData?.error?.message;
      if (Array.isArray(errorMessage)) {
        throw new Error(errorMessage.join(", "));
      } else if (errorMessage) {
        throw new Error(errorMessage);
      }
    } else if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

class AxiosInstance {
  async login(postData) {
    try {
      const response = await axiosInstance.post("/auth/login", postData);
      if (response.data.data.token) {
        setToken(response.data.data.token, false);
      }
      return response.data;
    } catch (error) {
      return { error };
    }
  }

  async refreshToken(isAdmin) {
    try {
      let response;
      response = await axiosInstance.get("/auth/refresh");
      if (response.data.data.accessToken) {
        setToken(response.data.data.accessToken, false);
      }
      if (isAdmin) {
        const adminToken = localStorage.getItem("adminToken");
        response = await axiosInstance.get("/auth/refresh", {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        });
        if (response.data.data.accessToken) {
          setToken(response.data.data.accessToken, true);
        }
      }
      return response.data;
    } catch (error) {
      toast.error(`${error.message}`); // Show toast on error
      return { error };
    }
  }

  async contactUs(postData) {
    try {
      const response = await axiosInstance.post("/contactus", postData);
      return response.data;
    } catch (error) {
      toast.error(`${error.message}`); // Show toast on error
      return { error };
    }
  }

  async getData(url) {
    try {
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      toast.error(`${error.message}`); // Show toast on error
      return { error };
    }
  }

  async postData(url, postData, headers = {}) {
    try {
      const response = await axiosInstance.post(url, postData, headers);
      return response.data;
    } catch (error) {
      toast.error(`${error.message}`); // Show toast on error
      return { error };
    }
  }

  async fetchClickSources(payload) {
    try {
      const response = await axiosInstance.post(
        "/ortho/click-sources",
        payload,
      );
      return response.data;
    } catch (error) {
      return { error };
    }
  }

  async updateData(url, postData) {
    try {
      const response = await axiosInstance.put(url, postData);
      return response.data;
    } catch (error) {
      toast.error(`${error.message}`); // Show toast on error
      return { error };
    }
  }

  async deleteData(url, postData) {
    try {
      const response = await axiosInstance.delete(url, postData);
      return response.data;
    } catch (error) {
      toast.error(`${error.message}`); // Show toast on error
      return { error };
    }
  }

  async generateUnhealthyImagesPdf(projectId) {
    try {
      const response = await axiosInstance.get(
        `/pdf-report/projects/${projectId}/report`,
        {
          responseType: "blob",
        },
      );
      // Extract filename from Content-Disposition header if available
      let filename = "Inspection Report.pdf";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        const matches = disposition.match(/filename="?([^";]+)"?/);
        if (matches && matches.length > 1) {
          filename = matches[1];
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      const message = await getBlobApiErrorMessage(error.response?.data);

      toast.error(message ?? `${error.message}`);
      return { error: message ? new Error(message) : error };
    }
  }

  async generateUnhealthyImagesPdfForImageIds(projectId, imageIds) {
    try {
      const response = await axiosInstance.get(
        `/pdf-report/projects/${projectId}/report`,
        {
          responseType: "blob",
        },
      );
      let filename = "Inspection Report.pdf";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        const matches = disposition.match(/filename="?([^";]+)"?/);
        if (matches && matches.length > 1) {
          filename = matches[1];
        }
      }
      return { blob: response.data, filename };
    } catch (error) {
      const message = await getBlobApiErrorMessage(error.response?.data);

      toast.error(message ?? `${error.message}`);
      return { error: message ? new Error(message) : error };
    }
  }

  async sharePdfReportBlob(pdfBlob, projectName) {
    try {
      const formData = new FormData();
      formData.append("pdf", pdfBlob, `${projectName}-report.pdf`);

      const response = await axiosInstance.post(
        "/pdf-report/share-blob",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return { shareUrl: response.data.shareUrl };
    } catch (error) {
      toast.error(`${error.message}`); // Show toast on error
      return { error };
    }
  }

  logout() {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Request canceled due to logout.");
    }
    localStorage.removeItem("token");
  }
}

const initializeAxios = new AxiosInstance();

export default initializeAxios;
